import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { snapshotsQuery } from '@/api/queries';
import { Button, Icon, Thumb, useToast, type IconName } from '@/design-system';
import { errorText } from '@/shell/errorText';
import { runSuggestion, saveStructure, type PinnedPreset } from './api';
import { useStructureLabels } from './labels';
import { PageStack, specOf } from './PageStack';
import type { StructureCtx } from './parts';
import { blankSection, primary, toServer } from './useStructure';
import st from './structure.module.css';
import s from './ai.module.css';

type Props = StructureCtx & {
  /** «233 дизайнов библиотеки» */
  libraryLabel: string;
  /** «Заменить»: List mode on that section */
  onReplace: (i: number) => void;
};

/**
 * «AI режим» (DESIGN_SPEC §4.6 AI mode): the PM describes the site; the AI picks the sections and
 * their designs from the library (P4-A02), using the approved research and the preferences when asked.
 * The result is applied at once: one row per section with its design and why, and «Заменить», which
 * opens List mode on it. The preview on the right is the page stack. The proposal comes from the
 * `ai.suggest_structure` job and is accepted by saving it with the reasons (P4-A02).
 */
export function AiMode({ site, doc, select, libraryLabel, onReplace }: Props) {
  const { t } = useTranslation('structure');
  const { t: tc } = useTranslation('common');
  const toast = useToast();
  const { section, designLabel } = useStructureLabels();
  const snapshot = useQuery(snapshotsQuery(site.organization.id)).data?.[0];
  const [prompt, setPrompt] = useState('');
  const [useResearch, setUseResearch] = useState(true);
  const [usePrefs, setUsePrefs] = useState(true);
  const [busy, setBusy] = useState(false);
  const [ran, setRan] = useState(false);
  const chips = t('ai.chips', { returnObjects: true }) as string[];
  const done = !busy && (ran || doc.secs.some((x) => x.ai_reason));

  const run = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const suggested = await runSuggestion(site.id, { prompt: prompt.trim() || null, use_research: useResearch, use_preferences: usePrefs });
      await doc.flush();
      // Accepting the proposal is saving it (P4-A02): the versions it chose, the reasons in `ai_reason`.
      const secs = suggested.map((x) => ({
        ...blankSection(x.section_type, x.preset_version_id ? [{ preset_version_id: x.preset_version_id } as PinnedPreset] : []),
        ai_reason: x.reason ? x.reason.slice(0, 300) : null,
      }));
      const out = await saveStructure(site.id, toServer(secs));
      doc.adopt(out);
      select(1);
      setRan(true);
      toast(t('ai.picked', { count: out.sections.length }));
    } catch (e) {
      toast(t('ai.failed', { error: errorText(e, tc) }));
    } finally {
      setBusy(false);
    }
  };

  const toggles: { on: boolean; set: (v: boolean) => void; label: string; icon: IconName }[] = [
    { on: useResearch, set: setUseResearch, label: snapshot ? t('ai.use_research', { version: snapshot.version }) : t('ai.use_research_none'), icon: 'scan-search' },
    { on: usePrefs, set: setUsePrefs, label: t('ai.use_prefs'), icon: 'sliders-horizontal' },
  ];

  return (
    <div className={st.aiGrid}>
      <div className={st.aiMain}>
        <div className={s.card}>
          <div className={s.head}>
            <h2 className={s.title}>{t('ai.title')}</h2>
            <span className={s.sub}>{t('ai.sub', { library: libraryLabel })}</span>
          </div>
          <textarea className={s.prompt} rows={5} aria-label={t('ai.prompt_label')} placeholder={t('ai.placeholder')} value={prompt} maxLength={2000} onChange={(e) => setPrompt(e.target.value)} />
          <div className={s.chips} role="group" aria-label={t('ai.chips_label')}>
            {chips.map((c) => (
              <button key={c} type="button" className={s.chip} onClick={() => setPrompt((v) => `${v ? `${v.trim()} ` : ''}${c}.`)}>
                {t('ai.chip', { text: c })}
              </button>
            ))}
          </div>
          <div className={s.runRow}>
            {toggles.map((o) => (
              <button key={o.label} type="button" className={s.toggle} aria-pressed={o.on} onClick={() => o.set(!o.on)}>
                <Icon name={o.on ? 'check' : o.icon} size={13} />
                {o.label}
              </button>
            ))}
            <Button icon="wand-sparkles" iconSize={15} className={s.run} onClick={() => void run()} aria-busy={busy} data-action="ai-run">
              {done ? t('ai.rerun') : t('ai.run')}
            </Button>
          </div>
        </div>
        {busy && (
          <div className={s.busy} aria-live="polite">
            <span className={s.spinner} />
            {t('ai.busy')}
          </div>
        )}
        {done && (
          <div className={s.result}>
            <div className={s.resultHead}>
              <h3 className={s.resultTitle}>{t('ai.done_title')}</h3>
              <span className={s.resultSub}>{t('ai.done_sub')}</span>
            </div>
            <div role="list" aria-label={t('ai.rows_label')}>
              {doc.secs.map((x, i) => {
                const p = primary(x);
                return (
                  <div key={x.key} role="listitem" className={s.row}>
                    <span className={s.rowThumb}>
                      <Thumb {...specOf(p)} className={s.fill} style={{ position: 'absolute' }} />
                    </span>
                    <span className={s.rowText}>
                      <span className={s.rowName}>
                        {section(x.section_type)} <span className={s.rowDesign}>· {p ? designLabel(p) : t('outline.ai')}</span>
                      </span>
                      <span className={s.why}>{x.ai_reason || t('ai.why_default')}</span>
                    </span>
                    <button type="button" className={s.replace} onClick={() => onReplace(i)}>
                      {t('ai.replace')}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      <div className={st.aiPreview}>
        <span className={st.caps}>{t('preview.title_short')}</span>
        <PageStack secs={doc.secs} onSelect={select} />
      </div>
    </div>
  );
}
